// status-row.ts — the SINGLE shared status-effect list row + list filler, used by BOTH the creature
// "Modify Status Effects" form (render-creature-status-form.ts) AND the inventory status form
// (index.ts -> ftRenderInventoryStatusList). It is the client-side unification of the per-row markup
// the PHP options/tools/statuses/list_group_caster_status_effect_inc.php used to render for both — so
// inventory and creature lists are now byte-for-byte the same UI and future changes happen in one place.
//
// Each row mirrors the PHP list_group: an L0/L1 rune icon (or PNG) + name (+ Paused badge) + origin
// line + start/end duration chips + turn-end flag icons + the full permission-gated button strip
// (stack ↓ N/M ↑ · edit · pause/resume · delete-one · delete-all-connections). The stack control reads
// window.FateTide.statusStackInfo (the StatusEffect::getStackInfo mirror emitted by ft_boot).
//
// Permission is passed IN (RowPerms) rather than re-derived here: the creature caller computes it from
// the cached blob (hasPlayerPermission/hasCampaignPermission); the inventory caller reads the
// server-stamped data-can-* attributes (the server stays the permission authority and re-validates
// every mutation).

import type { EditCtx, StatusEntry, StatusTemplateArr } from './types';
import { str, num, pick, flagOn } from './read';
import { iconSpan, iconHtml, clickableRef } from './dom';
import { renderStatusModifyForm } from './render-status-form';
import { creatureLabel, findCatalogStatusDef, resolvedStatusEntry, statusCampaignId, statusEncounterId } from './resolve-refs';
import { statusRunes, statusRuneMountHtml } from '../shared/status-rune';

/** Who may do what on a row. canEdit == player permission (edit/stack/pause/delete-one);
 *  canCampaign == campaign permission (the extra delete-all-connections button). */
export interface RowPerms {
    canEdit: boolean;
    canCampaign: boolean;
}

// ── Optimistic-delete reconciliation ───────────────────────────────────────────────────────────
// A deleted row is removed from the DOM immediately (assume the AJAX succeeds), but the delete's
// server round-trip + WS caster_update echo lag. If a SECOND status is deleted before the FIRST's
// echo lands, the first echo repaints this list from a cache snapshot that STILL contains the second
// (its own delete hasn't committed) — so the second row REAPPEARS until its own echo arrives (the
// reported "delete two back-to-back and the 2nd one comes back" bug). We hold optimistically-deleted
// status ids here and filter them out of EVERY repaint (fillStatusList) until the server confirms the
// delete (the id drops out of the incoming data) or a safety TTL lapses — the TTL lets a failed /
// permission-denied delete's row correctly come back instead of hiding forever.
const pendingDeletedStatusIds = new Map<string, number>(); // status_id -> expiry epoch ms
const PENDING_DELETE_TTL_MS = 12000;

interface MountedRow {
    node: HTMLElement;
    signature: string;
    structure: string;
    refresh(entry: StatusEntry): void;
}
const mountedLists = new WeakMap<HTMLElement, Map<string, MountedRow>>();
const rowRefreshers = new WeakMap<HTMLElement, (entry: StatusEntry) => void>();

/** Only this small row's inputs are retained. Definitions stay in the held catalog. */
function rowInputs(entry: StatusEntry, ctx: EditCtx, perms: RowPerms): { signature: string; structure: string } {
    const ref = str(pick(entry, 'status_item_id')) || str(entry.status_arr);
    const def: StatusTemplateArr = typeof entry.status_arr === 'object' && entry.status_arr !== null
        ? entry.status_arr : findCatalogStatusDef(ref) as StatusTemplateArr | null ?? { items: { item_id: ref } };
    const tmpl = def.items ?? {};
    const itemId = str(tmpl.item_id);
    const casterId = str(pick(entry, 'status_caster_id')) || (!('status_caster_id' in entry) ? str(pick(entry, 'status_target_id')) : '');
    const structure = JSON.stringify([str(pick(entry, 'status_id')), itemId, str(tmpl.item_name), str(tmpl.item_type),
        stackInfoFor(itemId), statusRunes(itemId, def) ? statusRuneMountHtml(def, { size: 25 }) : '',
        isPaused(entry), flagOn(pick(def, 'status_effect_pausable')), str(pick(entry, 'status_origin_id')),
        str(pick(entry, 'status_origin_id_item_name')), casterId, creatureLabel(casterId),
        statusCampaignId(entry, ctx.campaignId), statusEncounterId(ctx.encounterId), ctx, perms]);
    // Non-display instance fields also feed the Edit form; keep its closure current after a row delta.
    return { signature: JSON.stringify([structure, entry]), structure };
}

/** Mark a status as optimistically deleted so pending repaints don't resurrect its row. */
export function markStatusEffectDeleted(statusId: string): void {
    const sid = String(statusId ?? '');
    if (sid) pendingDeletedStatusIds.set(sid, Date.now() + PENDING_DELETE_TTL_MS);
}

interface StackInfo { base: string; level_index: number; is_base: boolean; max: number; quantity?: boolean; }

/** UUID -> stack info, from ft_boot (StatusEffect::getStackInfo mirror). Lookup key is uppercased. */
function stackInfoFor(itemId: string): StackInfo | null {
    const id = (itemId || '').toUpperCase();
    if (!id) return null;
    const map = window.FateTide?.statusStackInfo;
    const info = map && typeof map === 'object' ? map[id] : null;
    return info && typeof info === 'object' ? info as StackInfo : null;
}

/** status_instance_has_campaign_pause() twin — any status_paused_campaign_* component non-zero. */
export function isPaused(entry: StatusEntry): boolean {
    return (['year', 'month', 'day', 'hour', 'minute', 'second'] as const)
        .some((k) => num(pick(entry, `status_paused_campaign_${k}`)) > 0);
}

/**
 * Every crafting row is a PERMANENT worker row (progress lives on the separate crafting_progress
 * tracker attached to the WIP object, and membership is Work/Stop on that object's card), so a
 * crafting row offers no pause/resume — pause_status_effect_inc.php refuses a permanent status, so
 * the button would only ever error. Twin of the PHP list_group's `$lg_is_crafting`.
 */
const CRAFTING_STATUS_ID = '62ED7394-9C33-D41A-C426-1BC6723C999A';

export function isCraftingStatusEntry(entry: StatusEntry, def?: StatusTemplateArr): boolean {
    const tmplId = str((def?.items as Record<string, unknown> | undefined)?.item_id);
    return (str(pick(entry, 'status_item_id')) || tmplId).toUpperCase() === CRAFTING_STATUS_ID;
}

/**
 * Format a status instance's start/end campaign tuple as a {date,time} pair, or null when the whole
 * tuple is zero/absent (a permanent / passive status carries no time — e.g. Encumbered). Reads the
 * top-level `status_{starts,ends}_campaign_*` fields — the same slots the PHP list_group reads.
 */
export function fmtCampaign(entry: StatusEntry, prefix: 'starts' | 'ends'): { date: string; time: string } | null {
    const e = entry as Record<string, unknown>;
    const v = (['year', 'month', 'day', 'hour', 'minute', 'second'] as const).map((k) => num(e[`status_${prefix}_campaign_${k}`]));
    if (v.every((n) => n === 0)) return null;
    const pad = (n: number): string => String(n).padStart(2, '0');
    const [year = 0, month = 0, day = 0, hour = 0, minute = 0, second = 0] = v;
    return { date: `${year}-${pad(month)}-${pad(day)}`, time: `${pad(hour)}:${pad(minute)}:${pad(second)}` };
}

/**
 * A duration chip: the start/end status-effect icon + the campaign stamp split into a DATE badge and a
 * TIME badge with subtly different hues so it reads at a glance. A null stamp (permanent end) → the
 * permanent glyph instead of badges.
 */
/** A small sized glyph (no default 25px iconSpan bulk) so two chips fit on one row. */
function durGlyph(iconKey: string, size = 16): HTMLElement {
    const g = document.createElement('span');
    g.className = 'd-inline-flex align-items-center';
    try { g.innerHTML = iconMap(iconKey, size, size); } catch { /* leave empty on miss */ }
    return g;
}

export function durChip(iconKey: string, stamp: { date: string; time: string } | null): HTMLElement {
    const chip = document.createElement('span');
    // Compact so START + END fit side by side on ONE row: tight padding, small glyph, small badges.
    chip.className = 'd-inline-flex align-items-center gap-1 rounded';
    chip.style.padding = '0.1rem 0.4rem';
    // Soft tinted container so START (green) vs END (red) reads at a glance — gentle left-fading
    // gradient + a subtle matching border, intentionally low-intensity (not a loud solid fill).
    const isStart = iconKey === 'start';
    chip.style.background = isStart
        ? 'linear-gradient(90deg, rgba(25,135,84,.28), rgba(25,135,84,.05))'
        : 'linear-gradient(90deg, rgba(220,53,69,.28), rgba(220,53,69,.05))';
    chip.style.border = `1px solid ${isStart ? 'rgba(25,135,84,.55)' : 'rgba(220,53,69,.55)'}`;
    chip.appendChild(durGlyph(iconKey));
    if (stamp) {
        const mkBadge = (cls: string, text: string): HTMLElement => {
            const b = document.createElement('span');
            b.className = `badge rounded-pill ${cls} fw-normal`;
            b.style.fontSize = '0.7rem';
            b.style.padding = '0.15em 0.45em';
            b.textContent = text;
            return b;
        };
        chip.appendChild(mkBadge('bg-secondary-subtle text-secondary-emphasis', stamp.date));
        chip.appendChild(mkBadge('bg-info-subtle text-info-emphasis', stamp.time));
    } else {
        chip.appendChild(durGlyph('status_effect_permanent'));
    }
    return chip;
}

/** turn-end flag icons (list_group lines 116-120): which target/caster turn boundary ends this status. */
const TURN_END_FLAGS: ReadonlyArray<[string, string]> = [
    ['status_ends_on_target_start_of_turn', 'effect_ends_on_start_of_target_turn'],
    ['status_ends_on_target_end_of_turn', 'effect_ends_on_end_of_target_turn'],
    ['status_ends_on_caster_start_of_next_turn', 'effect_ends_on_start_of_caster_next_turn'],
    ['status_ends_on_caster_end_of_next_turn', 'effect_ends_on_end_of_caster_next_turn'],
];

/** A small icon button used in the action strip (matches the PHP list_group button sizing/classes). */
function actionBtn(cls: string, iconKey: string, fallbackText: string, title: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `btn btn-sm ${cls}`;
    b.title = title;
    b.setAttribute('aria-label', title);
    b.innerHTML = iconHtml(iconKey) || fallbackText;
    b.addEventListener('click', onClick);
    return b;
}

/**
 * Build ONE status-effect row. `entry` should already be resolved (resolvedStatusEntry) — fillStatusList
 * does that — but we read defensively either way.
 */
export function statusRow(entry: StatusEntry, ctx: EditCtx, perms: RowPerms): HTMLElement {
    // Vertical stack: (1) icon + name, (2) start/end chips side by side, (3) buttons — each its own row.
    const row = document.createElement('div');
    row.className = 'border rounded p-2 mb-2';
    let currentEntry = entry;

    const def: StatusTemplateArr = typeof entry.status_arr === 'object' ? entry.status_arr : {};
    const tmpl = (def.items ?? {}) as Record<string, unknown>;
    const itemId = str(tmpl.item_id);
    const itemType = str(tmpl.item_type);
    const statusId = str(pick(entry, 'status_id'));
    const paused = isPaused(entry);
    // Address the row by its instance id, the twin of the PHP list_group's `id="li_<status_id>"`.
    if (statusId) row.dataset.ftStatusId = statusId;

    // A crafting row offers no pause/resume (see isCraftingStatusEntry) and its Delete is "walk
    // away" — the endpoint settles the accrued time first and the WIP + its reagents survive.
    // Retired 2026-07-28: the Phase-2 primary/assist split, its Join/Leave buttons, the speed
    // badge, and the abandon-confirm.
    const isCrafting = isCraftingStatusEntry(entry, def);

    // Campaign + encounter context for the pause/resume gate. NEVER read the campaign off ctx alone:
    // buildEditCtx used to resolve it from keys that do not exist on a caster blob, so it was
    // permanently '' and the Pause button could never render (fixed 2026-07-28). Both endpoints
    // require BOTH ids, and both are forwarded to the mutation below as overrides — so the button
    // is shown exactly when the mutation it triggers will have the context it needs.
    const campaignId = statusCampaignId(entry, ctx.campaignId);
    const encounterId = statusEncounterId(ctx.encounterId);

    // ── Icon: L0/L1 rune for ability-derived statuses (no registered PNG), else the status PNG. The
    // <button> wrapper makes the footer rune scanner skip its own openRuneInfo wiring (it skips mounts
    // inside a <button>), so the click stays openItemInfo(statusId) — exactly like creature-console.ts.
    const iconBtn = document.createElement('button');
    iconBtn.type = 'button';
    iconBtn.className = 'btn p-0 border-0 bg-transparent shadow-none lh-1 d-flex align-items-center flex-shrink-0';
    if (itemId && statusRunes(itemId, def)) {
        iconBtn.innerHTML = statusRuneMountHtml(def, { size: 25 });
    } else {
        iconBtn.appendChild(iconSpan(itemId || itemType || 'status_effect'));
    }
    if (statusId) iconBtn.addEventListener('click', () => { if (typeof openItemInfo === 'function') openItemInfo(statusId); });

    // ── Row 1: icon + name (name is on its own dedicated row now — never truncated). ──
    const topRow = document.createElement('div');
    topRow.className = 'd-flex align-items-center gap-2 mb-1';
    topRow.appendChild(iconBtn);

    const nameCol = document.createElement('div');
    nameCol.className = 'flex-grow-1';

    const nameLine = document.createElement('div');
    nameLine.className = 'd-flex align-items-center gap-2 flex-wrap';
    const name = document.createElement('span');
    name.className = 'fw-semibold';
    name.textContent = str(tmpl.item_name) || 'Status Effect';
    // QUANTITY-stack families (burning / corroding / bleeding): the row carries `status_stack`;
    // show it inline — "Burning (3)" — the way the leveled rows carry their level in the item name.
    const qStack = stackInfoFor(itemId)?.quantity ? Math.max(1, num(pick(entry, 'status_stack')) || 1) : 1;
    if (qStack > 1) name.textContent += ` (${qStack})`;
    nameLine.appendChild(name);
    if (paused) {
        const badge = document.createElement('span');
        badge.className = 'badge bg-secondary';
        badge.textContent = 'Paused';
        nameLine.appendChild(badge);
    }
    nameCol.appendChild(nameLine);

    // Origin (where the status came from — an ability/spell/curse), if recorded.
    const originId = str(pick(entry, 'status_origin_id'));
    if (originId) {
        const originName = str(pick(entry, 'status_origin_id_item_name'));
        const oLine = document.createElement('div');
        oLine.className = 'small text-muted d-flex align-items-center gap-1';
        oLine.appendChild(iconSpan('status_effect_origin'));
        const oRef = document.createElement('span');
        oRef.className = 'link-primary';
        oRef.setAttribute('role', 'button');
        oRef.textContent = originName || 'Origin';
        oRef.addEventListener('click', () => { if (typeof openItemInfo === 'function') openItemInfo(originId); });
        oLine.appendChild(oRef);
        nameCol.appendChild(oLine);
    }

    // Caster (who applied it) — a small badged line under the name, ABOVE the duration (owner 2026-06-26:
    // show who cast the effect in the Modify Status Effects list, like the single-status form already does).
    const casterId = str(pick(entry, 'status_caster_id'));
    if (casterId) {
        const cLine = document.createElement('div');
        cLine.className = 'small d-flex align-items-center gap-1 mt-1';
        const badge = document.createElement('span');
        badge.className = 'badge bg-primary d-inline-flex align-items-center';
        badge.appendChild(iconSpan('caster'));
        cLine.appendChild(badge);
        cLine.appendChild(clickableRef(casterId, 'Unknown', ''));
        nameCol.appendChild(cLine);
    }

    topRow.appendChild(nameCol);
    row.appendChild(topRow);

    // ── Row 2: start + end chips side by side (their own full-width row) + turn-end flag icons. ──
    const start = fmtCampaign(entry, 'starts');
    const end = fmtCampaign(entry, 'ends');
    const dur = document.createElement('div');
    dur.className = 'small text-muted d-flex flex-wrap align-items-center mb-2';
    dur.style.columnGap = '0.5rem';
    dur.style.rowGap = '0.2rem';
    if (start) dur.appendChild(durChip('start', start));
    dur.appendChild(durChip('end', end));
    for (const [field, iconKey] of TURN_END_FLAGS) {
        if (flagOn(pick(entry, field))) dur.appendChild(iconSpan(iconKey));
    }
    row.appendChild(dur);

    // ── Row 3: action buttons on their own row (permission-gated). ──
    const actions = document.createElement('div');
    actions.className = 'd-flex flex-wrap align-items-center gap-1';
    let stackLevel: HTMLElement | undefined;
    let stackUp: HTMLButtonElement | undefined;

    if (perms.canEdit) {
        // Stack ↓ N/M ↑ — only for stacked-group statuses (window.FateTide.statusStackInfo).
        const stack = stackInfoFor(itemId);
        if (stack) {
            // Quantity model: N is the instance's status_stack; leveled model: N is the level row.
            const level = stack.quantity ? qStack : (stack.is_base ? 1 : stack.level_index + 1);
            const max = stack.max;
            actions.appendChild(actionBtn('btn-outline-secondary border shadow-none', '', '↓', 'Decrease stack',
                () => { if (typeof adjustStackableStatusEffect === 'function') adjustStackableStatusEffect('down', statusId, ctx.campaignId, ctx.encounterId); }));
            const lvl = document.createElement('span');
            stackLevel = lvl;
            lvl.className = 'small text-muted text-nowrap';
            lvl.textContent = `${level}/${max}`;
            actions.appendChild(lvl);
            const up = actionBtn('btn-outline-secondary border shadow-none', '', '↑', 'Increase stack',
                () => { if (typeof adjustStackableStatusEffect === 'function') adjustStackableStatusEffect('up', statusId, ctx.campaignId, ctx.encounterId); });
            if (max > 0 && level >= max) up.setAttribute('disabled', 'disabled');
            stackUp = up;
            actions.appendChild(up);
        }

        // Edit — open the single-status modify form CLIENT-SIDE (no AJAX to display it). Forward the
        // RESOLVED campaign id, not ctx's, so the sub-form's own pause/resume gate sees it too.
        actions.appendChild(actionBtn('btn-primary', 'modify_status_effect', 'Edit', 'Edit', () => {
            const host = document.getElementById('div_refresh_sidelog_form');
            if (host) renderStatusModifyForm(currentEntry, { ...ctx, statusId, campaignId }, host);
        }));

        // Pause / Resume — only when the campaign context exists and the status template is pausable.
        // Crafting worker rows are PERMANENT (pause_status_effect_inc.php refuses a permanent status),
        // so pausing one would only error server-side; they Stop from the WIP object card instead.
        const pausable = flagOn(pick(def, 'status_effect_pausable'));
        if (campaignId && encounterId && pausable && !isCrafting) {
            if (!paused) {
                actions.appendChild(actionBtn('btn-outline-secondary border', 'pause', '⏸', 'Pause status effect',
                    () => { if (typeof pauseStatusEffect === 'function') pauseStatusEffect(statusId, campaignId, encounterId); }));
            } else {
                actions.appendChild(actionBtn('btn-outline-success', 'resume', '▶', 'Resume status effect',
                    () => { if (typeof resumeStatusEffect === 'function') resumeStatusEffect(statusId, campaignId, encounterId); }));
            }
        }

        // Delete this instance (optimistic row removal; the WS caster_update reconciles the list). Mark
        // it pending FIRST so a concurrent delete's echo repaint can't resurrect this row before its own
        // echo lands (the back-to-back-delete race). Deleting a crafting WORKER row is "walk away" —
        // delete_creature_status_effect_inc.php settles the worker's accrued time first and the WIP
        // (with its reagents) survives — so it takes no confirm.
        actions.appendChild(actionBtn('btn-warning', 'delete_status_effect', 'Delete', 'Delete status effect', () => {
            markStatusEffectDeleted(statusId);
            if (typeof deleteCreatureStatusEffect === 'function') deleteCreatureStatusEffect(statusId, ctx.campaignId, ctx.encounterId);
            row.remove();
        }));
    }

    // NOTE: no Join button — joining a craft is Work on the WIP object card.

    // Delete-all-connections — GM-level (campaign permission), removes every linked instance.
    if (perms.canCampaign) {
        actions.appendChild(actionBtn('btn-danger', 'delete_status_effect_all_connections', 'Delete all', 'Delete status effect (all connections)', () => {
            markStatusEffectDeleted(statusId);
            if (typeof deleteCreatureStatusEffectAllConnections === 'function') deleteCreatureStatusEffectAllConnections(statusId, ctx.campaignId, ctx.encounterId);
            row.remove();
        }));
    }

    row.appendChild(actions);
    const durationInputs = (value: StatusEntry): string => JSON.stringify([
        fmtCampaign(value, 'starts'), fmtCampaign(value, 'ends'),
        TURN_END_FLAGS.map(([key]) => flagOn(pick(value, key))),
    ]);
    let duration = durationInputs(entry);
    rowRefreshers.set(row, next => {
        currentEntry = next;
        const stack = stackInfoFor(itemId);
        const count = stack?.quantity ? Math.max(1, num(pick(next, 'status_stack')) || 1) : 1;
        const text = (str(tmpl.item_name) || 'Status Effect') + (count > 1 ? ` (${count})` : '');
        if (name.textContent !== text) name.textContent = text;
        if (stack && stackLevel && stackUp) {
            const level = stack.quantity ? count : (stack.is_base ? 1 : stack.level_index + 1);
            const label = `${level}/${stack.max}`;
            if (stackLevel.textContent !== label) stackLevel.textContent = label;
            stackUp.disabled = stack.max > 0 && level >= stack.max;
        }
        const nextDuration = durationInputs(next);
        if (duration !== nextDuration) {
            duration = nextDuration;
            dur.replaceChildren();
            const start = fmtCampaign(next, 'starts');
            if (start) dur.appendChild(durChip('start', start));
            dur.appendChild(durChip('end', fmtCampaign(next, 'ends')));
            for (const [field, iconKey] of TURN_END_FLAGS) if (flagOn(pick(next, field))) dur.appendChild(iconSpan(iconKey));
        }
    });
    return row;
}

/** Apply exact saved-id row islands. Scalar stack/duration updates keep the row and its controls. */
export function patchStatusRows(listWrap: HTMLElement, add: StatusEntry[], update: StatusEntry[], remove: string[], ctx: EditCtx, perms: RowPerms, order?: readonly StatusEntry[]): void {
    let mounted = mountedLists.get(listWrap);
    if (!mounted) { mounted = new Map(); mountedLists.set(listWrap, mounted); }
    for (const id of remove) { mounted.get(id)?.node.remove(); mounted.delete(id); }
    for (const [index, entry] of [...add, ...update].entries()) {
        // Unsaved rows use their full-list position, including when only a later row changed.
        const id = str(pick(entry, 'status_id')) || String(order?.indexOf(entry) ?? index);
        const inputs = rowInputs(entry, ctx, perms);
        const previous = mounted.get(id);
        const resolved = resolvedStatusEntry(entry) ?? entry;
        if (previous && previous.node.parentElement === listWrap && previous.structure === inputs.structure) {
            previous.refresh(resolved);
            previous.signature = inputs.signature;
            continue;
        }
        const node = statusRow(resolved, ctx, perms);
        if (previous?.node.parentElement === listWrap) previous.node.replaceWith(node);
        else listWrap.appendChild(node);
        mounted.set(id, { node, ...inputs, refresh: rowRefreshers.get(node)! });
        try { window.ftMountRuneIcons?.(node); } catch { /* footer pass covers init */ }
    }
    if (mounted.size) listWrap.querySelector('[data-ft-status-empty]')?.remove();
    else if (!listWrap.querySelector('[data-ft-status-empty]')) {
        const empty = document.createElement('h6');
        empty.className = 'mb-3';
        empty.dataset.ftStatusEmpty = '';
        empty.textContent = 'No status effects';
        listWrap.appendChild(empty);
    }
}

/** Reconcile the held target bucket, preserving list/row identity and optimistic deletions. */
export function fillStatusList(listWrap: HTMLElement, statuses: StatusEntry[] | undefined, ctx: EditCtx, perms: RowPerms): void {
    const incoming = Array.isArray(statuses) ? statuses : [];

    // Reconcile the optimistic-delete registry against this fresh snapshot: an id that is GONE from
    // the incoming data means the server confirmed the delete (stop tracking it); an expired TTL means
    // give up waiting (a failed delete should reappear). Everything still tracked is filtered below so
    // a lagging echo can't resurrect a row the user already deleted (see markStatusEffectDeleted).
    let arr = incoming;
    if (pendingDeletedStatusIds.size) {
        const now = Date.now();
        const present = new Set(incoming.map((e) => String(pick(e, 'status_id') ?? '')));
        for (const [sid, expiry] of [...pendingDeletedStatusIds]) {
            if (!present.has(sid) || now >= expiry) pendingDeletedStatusIds.delete(sid);
        }
        if (pendingDeletedStatusIds.size) {
            arr = incoming.filter((e) => !pendingDeletedStatusIds.has(String(pick(e, 'status_id') ?? '')));
        }
    }

    if (!mountedLists.has(listWrap)) listWrap.replaceChildren();
    const mounted = mountedLists.get(listWrap);
    const add: StatusEntry[] = [], update: StatusEntry[] = [];
    const present = new Set<string>();
    for (const [index, entry] of arr.entries()) {
        const id = str(pick(entry, 'status_id')) || String(index);
        present.add(id);
        const previous = mounted?.get(id);
        if (!previous || previous.node.parentElement !== listWrap) add.push(entry);
        else if (previous.signature !== rowInputs(entry, ctx, perms).signature) update.push(entry);
    }
    patchStatusRows(listWrap, add, update, [...(mounted?.keys() ?? [])].filter(id => !present.has(id)), ctx, perms, arr);
    // Match incoming order by moving only a row whose position changed.
    let cursor = listWrap.firstElementChild;
    for (const id of present) {
        const node = mountedLists.get(listWrap)?.get(id)?.node;
        if (!node) continue;
        if (node !== cursor) listWrap.insertBefore(node, cursor);
        cursor = node.nextElementSibling;
    }
}
